import {ContractMenu} from "../../enums/contract/contract-menu.enum";
import {ContractModel} from "../../models/contract/contract.model";
import {Action, Selector, State, StateContext} from "@ngxs/store";
import {Injectable} from "@angular/core";
import {CompoApp} from "../../services/CompoApp";
import {finalize, from, Observable, of, throwError} from "rxjs";
import {delay, map} from "rxjs/operators";
import {RequestClientService} from "../../services/client/request-client.service";
import {ContractDetailAction} from "./contract-detail.actions";
import {ResetStateDefaults} from "../base-action-class.model";


interface ContractDetailStateModel {
  id: string;
  contract: ContractModel;
  selectedMenu: ContractMenu;
  inProgress: boolean;
}

const getDefaults = (): ContractDetailStateModel => ({
  id: null,
  contract: null,
  selectedMenu: ContractMenu.BASIC_INFO,
  inProgress: false
});

@State<ContractDetailStateModel>({
  name: 'contractDetail',
  defaults: getDefaults()
})
@Injectable()
export class ContractDetailXsState {

  constructor(
    private app: CompoApp,
  ) {
  }

  @Selector()
  static contract(state: ContractDetailStateModel): ContractModel | null {
    return state.contract || null;
  }

  @Selector()
  static selectedMenu(state: ContractDetailStateModel): ContractMenu | null {
    return state.selectedMenu || null;
  }

  @Selector()
  static inProgress(state: ContractDetailStateModel): boolean {
    return state.inProgress;
  }

  @Action(ContractDetailAction.LoadContract)
  public loadContract(
    ctx: StateContext<ContractDetailStateModel>,
    action: ContractDetailAction.LoadContract
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Contract request is in progress!'));
    }
    if (!action.id) {
      return throwError(() => new Error('Contract id must be set!'));
    }
    if (state.id === action.id) {
      return of(null);
    }

    ctx.patchState({
      id: null,
      contract: null
    });

    return from(
      this.app.contractService.getById(action.id)
    ).pipe(
      delay(RequestClientService.DEFAULT_TIMEOUT),
      map((contract: ContractModel) => {
        ctx.patchState({
          id: contract.id,
          contract: contract,
        });
      })
    );
  }

  @Action(ContractDetailAction.UpdateBasicInfo)
  public updateBasicInfo(
    ctx: StateContext<ContractDetailStateModel>,
    action: ContractDetailAction.UpdateBasicInfo
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Contract request is in progress!'));
    }
    if (!action.basicInfo) {
      return throwError(() => new Error('Basic info must be set!'));
    }
    if (!action.basicInfo.name) {
      return throwError(() => new Error('Name must be set!'));
    }

    ctx.patchState({
      inProgress: true
    });

    return from(this.app.contractService.update(state.id, action.basicInfo))
      .pipe(
        map((contract) => {
          ctx.patchState({contract});
        }),
        finalize(() => {
          ctx.patchState({inProgress: false});
        })
      );
  }

  @Action(ContractDetailAction.UpdateOrgIds)
  public updateOrgIds(
    ctx: StateContext<ContractDetailStateModel>,
    action: ContractDetailAction.UpdateOrgIds
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Contract request is in progress!'));
    }
    if (!action.orgIds) {
      return throwError(() => new Error('Org ids must be set!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const contract: ContractModel = {
      ...state.contract,
      selectedOrganizations: null,
      orgIds: action.orgIds
    };
    contract.removedOrgIds = action.removedOrgIds;

    return from(this.app.contractService.update(state.id, contract))
      .pipe(
        map((contract) => {
          ctx.patchState({contract});
        }),
        finalize(() => {
          ctx.patchState({inProgress: false});
        })
      );
  }

  @Action(ContractDetailAction.Delete)
  public delete(ctx: StateContext<ContractDetailStateModel>): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Contract request is in progress!'));
    }

    ctx.patchState({
      inProgress: true
    });

    return from(this.app.contractService.delete(state.id))
      .pipe(
        map(() => {
          ctx.patchState({contract: null, id: null});
        }),
        finalize(() => {
          ctx.patchState({inProgress: false});
        })
      );
  }

  @Action(ContractDetailAction.SelectMenu)
  public selectMenu(
    ctx: StateContext<ContractDetailStateModel>,
    action: ContractDetailAction.SelectMenu
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Contract request is in progress!'));
    }
    if (!action.menu) {
      return throwError(() => new Error('Menu must be set!'));
    }

    ctx.patchState({
      selectedMenu: action.menu
    });
  }

  @Action([ContractDetailAction.ResetState, ResetStateDefaults])
  public resetState(ctx: StateContext<ContractDetailStateModel>): Observable<void> {
    ctx.patchState({...getDefaults()});
    return of(null);
  }

}
